/**
 * 🎟️ STADIUM ENTRY PAGE
 * What fans see when they scan the QR code!
 * Instant onboarding - zero friction!
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, IceCream, Gift, Camera, ChevronRight, Zap } from 'lucide-react';
import styles from './Entry.module.css';

export function EntryPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [installing, setInstalling] = useState(false);

  // Auto-advance welcome animation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (step < 3) setStep(step + 1);
    }, step === 0 ? 1500 : 800);
    return () => clearTimeout(timer);
  }, [step]);

  // Check if can install as PWA
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  
  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      setInstalling(true);
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('Install outcome:', outcome);
      setDeferredPrompt(null);
      setInstalling(false);
    }
    // Continue anyway
    navigate('/fun');
  };

  const handleSkip = () => {
    navigate('/fun');
  };

  return (
    <div className={styles.container}>
      {/* Animated background */}
      <div className={styles.bgGlow} />
      
      {/* Welcome Animation */}
      <div className={styles.welcome}>
        <div className={`${styles.welcomeItem} ${step >= 1 ? styles.visible : ''}`}>
          <span className={styles.welcomeEmoji}>🏟️</span>
          <span>Welcome to</span>
        </div>
        <div className={`${styles.welcomeItem} ${step >= 2 ? styles.visible : ''}`}>
          <span className={styles.welcomeEmoji}>🎨</span>
          <span>PEOPLE PIXELS!</span>
        </div>
      </div>

      {/* Main Content - appears after animation */}
      {step >= 3 && (
        <div className={styles.content}>
          {/* Hero Image */}
          <div className={styles.heroImage}>
            <img src="/peoplepixels.jpg" alt="People Pixels" />
            <div className={styles.heroOverlay}>
              <span>This could be YOU!</span>
            </div>
          </div>

          {/* Features */}
          <div className={styles.features}>
            <div className={styles.feature}>
              <div className={styles.featureIcon}>
                <Sparkles size={24} />
              </div>
              <div className={styles.featureText}>
                <h3>Be Part of the Show</h3>
                <p>Your phone becomes a pixel!</p>
              </div>
            </div>
            
            <div className={styles.feature}>
              <div className={styles.featureIcon}>
                <IceCream size={24} />
              </div>
              <div className={styles.featureText}>
                <h3>Order Food Fast</h3>
                <p>Skip the queue, delivered to seat!</p>
              </div>
            </div>
            
            <div className={styles.feature}>
              <div className={styles.featureIcon}>
                <Gift size={24} />
              </div>
              <div className={styles.featureText}>
                <h3>Win Rewards</h3>
                <p>Participate = discounts & prizes!</p>
              </div>
            </div>
            
            <div className={styles.feature}>
              <div className={styles.featureIcon}>
                <Camera size={24} />
              </div>
              <div className={styles.featureText}>
                <h3>Get the Photo</h3>
                <p>Drone shot sent to your phone!</p>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className={styles.actions}>
            <button 
              className={styles.primaryBtn}
              onClick={handleInstall}
              disabled={installing}
            >
              <Zap size={20} />
              <span>{installing ? 'Setting up...' : "LET'S GO!"}</span>
              <ChevronRight size={20} />
            </button>
            
            {deferredPrompt && (
              <p className={styles.installHint}>
                📱 Add to home screen for best experience!
              </p>
            )}
            
            <button className={styles.skipBtn} onClick={handleSkip}>
              Skip intro
            </button>
          </div>

          {/* How it works mini */}
          <div className={styles.howItWorks}>
            <span>📳📳📳</span>
            <span>→</span>
            <span>📱☁️</span>
            <span>→</span>
            <span>📸🎁</span>
          </div>
        </div>
      )}
    </div>
  );
}
